import { Component } from '@theme/component';

/** Interval tussen twee beloftes, en de duur van het wegschuiven (uit het ontwerp). */
const INTERVAL_MS = 3500;
const SLIDE_MS = 700;
const MOBILE_QUERY = '(max-width: 749px)';

/**
 * Laat de beloftes in de aankondigingsbalk op mobiel één voor één inschuiven.
 * Achter de laatste staat een (verborgen) kopie van de eerste, zodat de rij
 * na een volle ronde ongemerkt terug kan springen naar het begin.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - De rij met beloftes.
 *
 * @extends {Component<Refs>}
 */
class TrustBarTicker extends Component {
  requiredRefs = ['track'];

  /** @type {number | undefined} */
  #timer;

  #index = 0;

  #query = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    super.connectedCallback();
    this.#query.addEventListener('change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#query.removeEventListener('change', this.#sync);
    this.#stop();
  }

  #sync = () => {
    const items = this.#items();
    if (this.#query.matches && items.length > 1) this.#start(items);
    else this.#stop();
  };

  /** @returns {HTMLElement[]} De echte beloftes, zonder de kopie. */
  #items() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.refs.track.children).filter((child) => !child.hasAttribute('data-ticker-clone'))
    );
  }

  /** @param {HTMLElement[]} items */
  #start(items) {
    if (this.#timer) return;
    const first = items[0];
    if (!first) return;

    const clone = /** @type {HTMLElement} */ (first.cloneNode(true));
    clone.setAttribute('data-ticker-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    this.refs.track.append(clone);
    this.setAttribute('data-ticking', '');

    this.#index = 0;
    this.#timer = window.setInterval(() => this.#advance(items.length), INTERVAL_MS);
  }

  /** @param {number} count */
  #advance(count) {
    const { track } = this.refs;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#index += 1;
    track.style.transition = still ? 'none' : `transform ${SLIDE_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`;
    track.style.transform = `translateX(${-this.#index * 100}%)`;

    if (this.#index === count) {
      window.setTimeout(
        () => {
          track.style.transition = 'none';
          track.style.transform = 'translateX(0)';
          this.#index = 0;
        },
        still ? 0 : SLIDE_MS + 20
      );
    }
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
    this.refs.track.querySelector('[data-ticker-clone]')?.remove();
    this.refs.track.style.transform = '';
    this.refs.track.style.transition = '';
    this.removeAttribute('data-ticking');
  }
}

if (!customElements.get('trust-bar-ticker')) {
  customElements.define('trust-bar-ticker', TrustBarTicker);
}
